import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Calendar, Clock, Users, Video, Check, ArrowRight, CreditCard, User, Link2, CheckCircle } from 'lucide-react';
import SlotPickerModal from './SlotPickerModal';
import { trackEvent } from '../../utils/analytics';
import { useCoach } from '../../contexts/CoachContext';

const getLevelColor = (level) => {
  switch ((level || '').toLowerCase()) {
    case 'beginner':
      return { bg: '#10b981', text: '#ffffff' };
    case 'intermediate':
      return { bg: '#f59e0b', text: '#ffffff' };
    case 'advanced':
      return { bg: '#ef4444', text: '#ffffff' };
    default:
      return { bg: '#749555', text: '#ffffff' };
  }
};

/**
 * Format 24-hour time to 12-hour format
 */
const formatSlotTime = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

/**
 * Format date for individual sessions
 */
const formatIndividualDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format lesson data to handle both legacy format and new Firestore format
 */
const formatLessonData = (lesson) => {
  // If it's the new Firestore format with displayTimes
  if (lesson.displayTimes) {
    const spotsBooked = lesson.spotsBooked || 0;
    const spotsAvailable = Math.max(0, (lesson.spotsTotal || 0) - spotsBooked);

    return {
      ...lesson,
      date: {
        display: lesson.displayTimes.coach?.date || 'TBD',
        times: [
          { tz: lesson.displayTimes.coach?.label || 'Local', time: lesson.displayTimes.coach?.time || '' },
          { tz: 'EST', time: lesson.displayTimes.EST?.time || '' },
          { tz: 'CET', time: lesson.displayTimes.CET?.time || '' },
        ].filter(t => t.time), // Filter out empty times
      },
      instructor: lesson.instructor ? (lesson.instructor.startsWith('with') ? lesson.instructor : `with ${lesson.instructor}`) : '',
      spotsAvailable,
      priceUSD: 15, // Default - will be replaced by package pricing
      priceZAR: 270, // Default
    };
  }

  // Return as-is if it's already in legacy format
  return lesson;
};

/**
 * Check if user can join the session (20 mins before to 3 hrs after)
 */
const canJoinSession = (lesson) => {
  if (!lesson.scheduledAtUTC) return { canJoin: false, minutesUntilOpen: null };

  const scheduledDate = lesson.scheduledAtUTC?.toDate?.() || new Date(lesson.scheduledAtUTC);
  const now = new Date();

  // Students can join 20 mins before
  const windowMinutes = 20;
  const earliestJoin = new Date(scheduledDate.getTime() - windowMinutes * 60 * 1000);
  const sessionEnd = new Date(scheduledDate.getTime() + 3 * 60 * 60 * 1000);

  if (now < earliestJoin) {
    const minutesUntilOpen = Math.ceil((earliestJoin - now) / (60 * 1000));
    return { canJoin: false, minutesUntilOpen };
  }

  if (now > sessionEnd) {
    return { canJoin: false, minutesUntilOpen: null };
  }

  return { canJoin: true, minutesUntilOpen: 0 };
};

/**
 * Check if registration is closed (30 mins after session start)
 */
const isRegistrationClosed = (lesson) => {
  if (!lesson.scheduledAtUTC) return false;

  const scheduledDate = lesson.scheduledAtUTC?.toDate?.() || new Date(lesson.scheduledAtUTC);
  const now = new Date();
  const cutoffTime = new Date(scheduledDate.getTime() + 3 * 60 * 60 * 1000); // 3 hrs after start

  return now > cutoffTime;
};

/**
 * LessonCarouselCard - Display a single lesson in the carousel
 * Handles both Group lessons and Individual (1-on-1) sessions
 * @param {Object} props
 * @param {Object} props.lesson - Lesson data
 * @param {Object} [props.currentUser] - Current authenticated user (optional)
 * @param {number} [props.userCredits] - User's credit balance for this coach (optional)
 * @param {boolean} [props.isEnrolled] - Whether user is enrolled in this lesson (optional)
 * @param {Function} [props.onRegister] - Callback when user registers (optional)
 * @param {Function} [props.onBookSlot] - Callback when user books an individual slot (optional)
 */
const LessonCarouselCard = ({
  lesson: rawLesson,
  currentUser = null,
  userCredits = 0,
  isEnrolled = false,
  onRegister = null,
  onBookSlot = null
}) => {
  const navigate = useNavigate();
  const { username: paramUsername } = useParams();
  const coachContext = useCoach();
  const username = paramUsername || coachContext?.username;
  const [showSlotPicker, setShowSlotPicker] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Detect session type
  const isIndividual = rawLesson.sessionType === 'individual';

  const lesson = formatLessonData(rawLesson);

  // Build the correct checkout URL using username
  const baseUrl = username ? `/coach/${username}` : '/coach';

  // Individual session helpers
  const availableSlots = isIndividual ? (rawLesson.slots || []).filter(s => s.status !== 'booked') : [];
  const hasAvailableSlots = availableSlots.length > 0;

  // Check if user has booked a slot in this individual session
  const userBookedSlotIndex = isIndividual && currentUser
    ? (rawLesson.slots || []).findIndex(s => s.bookedBy?.odaUserId === currentUser.uid)
    : -1;
  const isBookedByUser = userBookedSlotIndex >= 0;

  // Determine button state
  const { canJoin, minutesUntilOpen } = canJoinSession(rawLesson);
  const hasDailyRoom = !!rawLesson.dailyRoomName || !!rawLesson.dailyRoomUrl;

  const handleRegister = () => {
    trackEvent('Book Lesson', { coach: username, lessonType: 'group' });

    // Not logged in → redirect to auth
    if (!currentUser) {
      navigate(`/auth?context=student&redirect=${encodeURIComponent(`${baseUrl}/checkout?lessonId=${lesson.id}`)}`);
      return;
    }

    // Logged in but no credits → redirect to checkout
    if (userCredits <= 0) {
      navigate(`${baseUrl}/checkout?lessonId=${lesson.id}`);
      return;
    }

    // Logged in with credits → register directly
    if (onRegister) {
      onRegister(lesson.id);
    }
  };

  const handleJoinSession = () => {
    navigate(`${baseUrl}/session/${lesson.id}`);
  };

  // Individual session handlers
  const handleBookClick = () => {
    if (!hasAvailableSlots) return;
    trackEvent('Book Lesson', { coach: username, lessonType: 'individual' });
    setShowSlotPicker(true);
  };

  const handleConfirmBooking = (slotIndex) => {
    // Not logged in → redirect to auth
    if (!currentUser) {
      navigate(`/auth?context=student&redirect=${encodeURIComponent(`${baseUrl}/individual/checkout?lessonId=${rawLesson.id}&slotIndex=${slotIndex}`)}`);
      return;
    }

    // Logged in → proceed to checkout
    if (onBookSlot) {
      onBookSlot(rawLesson.id, slotIndex);
    } else {
      navigate(`${baseUrl}/individual/checkout?lessonId=${rawLesson.id}&slotIndex=${slotIndex}`);
    }
    setShowSlotPicker(false);
  };

  const handleCopyLessonLink = (e) => {
    e.stopPropagation();
    const lessonId = rawLesson.id || lesson.id;
    const url = `${window.location.origin}/coach/${username}/lesson/${lessonId}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).catch(() => {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      });
    } else {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Check if registration is closed for this lesson
  const registrationClosed = isRegistrationClosed(rawLesson);

  // Render the appropriate button based on state
  const renderButton = () => {
    // Individual session button logic
    if (isIndividual) {
      if (isBookedByUser) {
        // User has booked this session - check if can join
        const bookedSlot = rawLesson.slots[userBookedSlotIndex];
        const sessionDate = new Date(rawLesson.date + 'T' + bookedSlot.startTime);
        const now = new Date();
        const windowMinutes = 20;
        const earliestJoin = new Date(sessionDate.getTime() - windowMinutes * 60 * 1000);
        const sessionEnd = new Date(sessionDate.getTime() + (rawLesson.slotDuration || 60) * 60 * 1000);
        const individualCanJoin = now >= earliestJoin && now <= sessionEnd && !!rawLesson.dailyRoomName;

        if (individualCanJoin) {
          return (
            <button className="register-btn join-zoom-btn" onClick={handleJoinSession}>
              <Video size={18} />
              Join Session
            </button>
          );
        } else {
          return (
            <button className="register-btn registered-btn individual" disabled>
              <Check size={18} />
              Booked - {formatSlotTime(bookedSlot.startTime)}
            </button>
          );
        }
      }

      // Not booked → Show book button (yellow, same as group lessons)
      return (
        <button
          className="register-btn"
          onClick={handleBookClick}
          disabled={!hasAvailableSlots}
        >
          {hasAvailableSlots ? (
            <>
              <CreditCard size={18} />
              Select Time & Book
            </>
          ) : (
            'Fully Booked'
          )}
        </button>
      );
    }

    // Group lesson button logic
    if (isEnrolled) {
      if (canJoin && hasDailyRoom) {
        // Enrolled + can join → Join Session button
        return (
          <button className="register-btn join-zoom-btn" onClick={handleJoinSession}>
            <Video size={18} />
            Join Session
          </button>
        );
      } else if (minutesUntilOpen) {
        // Enrolled but too early → Show time until open
        const timeText = minutesUntilOpen > 60
          ? `Opens 20 mins before start`
          : `Class opens in ${minutesUntilOpen} min`;
        return (
          <button className="register-btn registered-btn" disabled>
            <Check size={18} />
            {timeText}
          </button>
        );
      } else {
        // Enrolled, no Daily room or session ended
        return (
          <button className="register-btn registered-btn" disabled>
            <Check size={18} />
            Registered
          </button>
        );
      }
    }

    // Not enrolled - check if fully booked
    if (lesson.spotsAvailable === 0) {
      return (
        <button className="register-btn closed-btn" disabled>
          Fully Booked
        </button>
      );
    }

    // Not enrolled - check if registration is closed
    if (registrationClosed) {
      return (
        <button className="register-btn closed-btn" disabled>
          Live - Registration Closed
        </button>
      );
    }

    // Not enrolled → Register button
    return (
      <button className="register-btn" onClick={handleRegister}>
        <CreditCard size={18} />
        Register for Lesson
      </button>
    );
  };

  return (
    <>
    <div className={`carousel-card ${isIndividual ? 'individual-card' : ''}`}>
      {/* Badge row - level badge + copy link */}
      <div className="card-badge-row">
        {isIndividual ? (
          <span className="level-badge individual-badge">
            <User size={12} />
            PRIVATE
          </span>
        ) : (
          <span className="level-badge">
            {lesson.ratingCap ? `Group - U${lesson.ratingCap}` : `Group - ${lesson.level}`}
          </span>
        )}
        {username && (
          <button
            className={`card-copy-link-btn${linkCopied ? ' copied' : ''}`}
            onClick={handleCopyLessonLink}
            title="Copy shareable link"
          >
            {linkCopied ? <><CheckCircle size={13} /> Link Copied!</> : <Link2 size={14} />}
          </button>
        )}
      </div>

      {/* Title - replace legacy "1-on-1 Session" with "Private Lessons" */}
      <h3 className="card-title">
        {isIndividual
          ? (rawLesson.title === '1-on-1 Session' || !rawLesson.title ? 'Private Lessons' : rawLesson.title)
          : lesson.title}
      </h3>

      {/* Instructor - for both group and individual */}
      {isIndividual ? (
        rawLesson.instructor && <p className="card-instructor">with {rawLesson.instructor}</p>
      ) : (
        lesson.instructor && <p className="card-instructor">{lesson.instructor}</p>
      )}

      {/* Date card */}
      <div className="date-card">
        <div className="date-header">
          <Calendar size={16} />
          <span>
            {isIndividual
              ? (rawLesson.displayTimes?.coach?.date || formatIndividualDate(rawLesson.date))
              : (lesson.date?.display || 'Date TBD')}
          </span>
        </div>
        {isIndividual ? (
          // Individual: always show all slot times so users see every available time
          (() => {
            const allSlots = rawLesson.slots || [];
            const tzLabel = rawLesson.displayTimes?.coach?.label;
            return allSlots.length > 0 ? (
              <div className="time-slots-preview">
                {tzLabel && <span className="slot-tz-label">{tzLabel}</span>}
                {allSlots.slice(0, 4).map((slot, idx) => (
                  <span key={idx} className={`slot-preview-chip${slot.status === 'booked' ? ' slot-booked' : ''}`}>
                    {formatSlotTime(slot.startTime)}
                  </span>
                ))}
                {allSlots.length > 4 && (
                  <span className="more-slots">+{allSlots.length - 4} more</span>
                )}
              </div>
            ) : rawLesson.displayTimes?.coach ? (
              <div className="time-zones">
                <span className="time-zone">
                  <strong>{rawLesson.displayTimes.coach?.label || 'Local'}</strong> {rawLesson.displayTimes.coach?.time || ''}
                </span>
              </div>
            ) : null;
          })()
        ) : (
          // Group: show timezone times
          lesson.date?.times?.length > 0 && (
            <div className="time-zones">
              {lesson.date.times.map((t, i) => (
                <span key={i} className="time-zone">
                  <strong>{t.tz}</strong> {t.time}
                </span>
              ))}
            </div>
          )
        )}
      </div>

      {/* Info boxes */}
      <div className="info-boxes">
        <div className="info-box">
          <Clock size={16} />
          <span>{isIndividual ? rawLesson.slotDuration : lesson.duration} min</span>
        </div>
        <div className="info-box">
          {isIndividual ? (
            // Individual: fixed USD price with ZAR equivalent
            <>
              <span className="price">${rawLesson.priceUsd}</span>
              <span className="price-divider">/</span>
              <span className="price-zar">R{Math.round(rawLesson.priceUsd * 18)}</span>
            </>
          ) : (
            // Group: credit-based pricing
            <>
              <span className="price">${lesson.priceUSD}</span>
              <span className="price-divider">/</span>
              <span className="price-zar">R{lesson.priceZAR}</span>
            </>
          )}
        </div>
      </div>

      {/* Register/Join/Registered button */}
      {renderButton()}

      {/* Availability indicator or Dashboard link */}
      {isIndividual ? (
        isBookedByUser ? (
          <Link to={`${baseUrl}/dashboard`} className="view-dashboard-link">
            <span>View Dashboard</span>
            <ArrowRight size={14} />
          </Link>
        ) : (
          <div className={`spots-indicator${availableSlots.length === 0 ? ' spots-none' : ''}`}>
            <Users size={14} />
            <span>
              {availableSlots.length === 0
                ? 'No slots available'
                : `${availableSlots.length} ${availableSlots.length === 1 ? 'slot' : 'slots'} available`}
            </span>
          </div>
        )
      ) : isEnrolled ? (
        <Link to={`${baseUrl}/dashboard?lessonId=${lesson.id}`} className="view-dashboard-link">
          <span>View Dashboard</span>
          <ArrowRight size={14} />
        </Link>
      ) : (
        <div className={`spots-indicator${lesson.spotsAvailable === 0 ? ' spots-none' : ''}`}>
          <Users size={14} />
          <span>
            {lesson.spotsAvailable === 0
              ? 'No spots available'
              : lesson.spotsAvailable != null
                ? `${lesson.spotsAvailable} spots available`
                : 'Open registration'}
          </span>
        </div>
      )}
    </div>

    {/* Slot Picker Modal - Individual Sessions Only */}
    <SlotPickerModal
      isOpen={isIndividual && showSlotPicker}
      onClose={() => setShowSlotPicker(false)}
      onConfirm={handleConfirmBooking}
      slots={rawLesson.slots || []}
      date={formatIndividualDate(rawLesson.date)}
      duration={rawLesson.slotDuration}
      price={rawLesson.priceUsd}
      formatSlotTime={formatSlotTime}
    />

      <style>{`
        .carousel-card {
          background: var(--gc-bg-card);
          border-radius: 16px;
          min-width: 380px;
          max-width: 380px;
          padding: 28px 24px 24px;
          position: relative;
          overflow: hidden;
          scroll-snap-align: start;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
        }

        .card-badge-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          margin-bottom: 16px;
        }

        .card-copy-link-btn {
          background: var(--gc-border-light, rgba(255, 255, 255, 0.05));
          border: 1px solid var(--gc-border, rgba(255, 255, 255, 0.1));
          color: var(--gc-text-light, #64748b);
          padding: 5px 9px;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 0.72rem;
          font-weight: 600;
          white-space: nowrap;
          transition: all 0.2s;
          font-family: inherit;
        }

        .card-copy-link-btn:hover {
          color: var(--gc-primary, #749555);
          border-color: var(--gc-primary, #749555);
          background: rgba(116, 149, 85, 0.1);
        }

        .card-copy-link-btn.copied {
          color: var(--gc-primary, #749555);
          border-color: var(--gc-primary, #749555);
          background: rgba(116, 149, 85, 0.1);
        }

        .level-badge {
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          background: transparent;
          border: 1.5px solid var(--gc-primary);
          color: var(--gc-primary);
        }

        .card-title {
          font-size: 1.35rem;
          font-weight: 700;
          color: var(--gc-text);
          margin-bottom: 8px;
          line-height: 1.3;
        }

        .card-instructor {
          font-size: 0.95rem;
          color: var(--gc-text-muted);
          margin-bottom: 20px;
        }

        .date-card {
          background: var(--gc-border-light);
          border: 1px solid var(--gc-border);
          border-radius: 12px;
          padding: 14px 16px;
          margin-bottom: 16px;
        }

        .date-header {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--gc-text);
          font-weight: 600;
          font-size: 0.95rem;
          margin-bottom: 10px;
        }

        .date-header svg {
          color: #60a5fa;
        }

        .time-zones {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .time-zone {
          font-size: 0.85rem;
          color: var(--gc-text-muted);
        }

        .time-zone strong {
          color: #60a5fa;
          font-weight: 600;
          margin-right: 4px;
        }

        .info-boxes {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
        }

        .info-box {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px;
          background: var(--gc-border-light);
          border: 1px solid var(--gc-border);
          border-radius: 10px;
          color: var(--gc-text);
          font-size: 0.9rem;
          font-weight: 500;
        }

        .info-box svg {
          color: var(--gc-text-muted);
        }

        .price {
          font-weight: 700;
          color: #fbbf24;
        }

        .price-divider {
          color: #4b5563;
          margin: 0 2px;
        }

        .price-zar {
          color: var(--gc-text-muted);
          font-weight: 500;
        }

        .register-btn {
          width: 100%;
          padding: 14px 24px;
          background: #fbbf24;
          color: #1a1a2e;
          border: none;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .register-btn:hover {
          background: #f59e0b;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(251, 191, 36, 0.3);
        }

        .register-btn.join-zoom-btn {
          background: #3b82f6;
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          animation: joinPulse 2s ease-in-out infinite;
        }

        .register-btn.join-zoom-btn:hover {
          background: #2563eb;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        @keyframes joinPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }

        .register-btn.registered-btn {
          background: var(--gc-border);
          color: var(--gc-text-muted);
          cursor: default;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .register-btn.registered-btn:hover {
          background: var(--gc-border);
          transform: none;
          box-shadow: none;
        }

        .register-btn.closed-btn {
          background: #6b7280;
          color: #ffffff;
          cursor: not-allowed;
        }

        .register-btn.closed-btn:hover {
          background: #6b7280;
          transform: none;
          box-shadow: none;
        }

        .spots-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: 0.85rem;
          color: #6b7280;
        }

        .spots-indicator svg {
          color: #6b7280;
        }

        .spots-indicator.spots-none {
          color: #ef4444;
        }

        .spots-indicator.spots-none svg {
          color: #ef4444;
        }

        .view-dashboard-link {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: 0.85rem;
          color: var(--gc-primary);
          font-weight: 500;
          transition: all 0.2s;
        }

        .view-dashboard-link:hover {
          color: #a3c47a;
        }

        .view-dashboard-link svg {
          transition: transform 0.2s;
        }

        .view-dashboard-link:hover svg {
          transform: translateX(3px);
        }

        /* Individual Session Styles */
        .individual-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .register-btn.registered-btn.individual {
          background: var(--gc-border);
          color: var(--gc-text-muted);
        }

        .price.individual-price {
          color: #fbbf24;
          font-weight: 700;
        }

        .time-slots-preview {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .slot-preview-chip {
          padding: 4px 10px;
          background: var(--gc-border-light);
          border: 1px solid var(--gc-border);
          border-radius: 16px;
          font-size: 0.8rem;
          color: var(--gc-text-muted);
        }

        .slot-preview-chip.slot-booked {
          opacity: 0.5;
          text-decoration: line-through;
        }

        .slot-tz-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: #60a5fa;
          margin-right: 2px;
        }

        .more-slots {
          padding: 4px 10px;
          font-size: 0.8rem;
          color: var(--gc-text-muted);
        }

        /* Slot Picker Modal styles now in SlotPickerModal.jsx */

        @media (max-width: 480px) {
          .carousel-card {
            min-width: 320px;
            max-width: 320px;
            padding: 24px 20px 20px;
          }

          .card-title {
            font-size: 1.2rem;
          }

          .time-zones {
            flex-direction: column;
            gap: 6px;
          }

        }
      `}</style>
    </>
  );
};

export default LessonCarouselCard;
